import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Footer from "../../components/Footer";
import { enterpriseFeatures } from "./EnterpriseFeatures";

// --- ★ UPDATED StickyTabNav (Wrapper Strategy for Perfect Stability) ★ ---
const StickyTabNav = ({ features, activeTab, onTabClick, mainNavHeight }) => {
  const containerRef = useRef(null); // Measure this wrapper, NOT the nav directly
  const activeTabRef = useRef(null);

  const [isSticky, setIsSticky] = useState(false);
  const [originalTop, setOriginalTop] = useState(0);
  const [navHeight, setNavHeight] = useState(0);

  // 1. ROBUST MEASUREMENT SYSTEM
  useEffect(() => {
    const updatePosition = () => {
      if (containerRef.current) {
        const top = containerRef.current.offsetTop;
        const height = containerRef.current.offsetHeight;

        // Update if the layout has shifted significantly
        if (Math.abs(top - originalTop) > 5) {
          setOriginalTop(top);
        }
        if (height > 0 && Math.abs(height - navHeight) > 2) {
          setNavHeight(height);
        }
      }
    };

    updatePosition();
    const timer1 = setTimeout(updatePosition, 200);
    const timer2 = setTimeout(updatePosition, 1000);
    const timer3 = setTimeout(updatePosition, 3000);

    window.addEventListener("resize", updatePosition);

    const resizeObserver = new ResizeObserver(() => updatePosition());
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    resizeObserver.observe(document.body);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener("resize", updatePosition);
      resizeObserver.disconnect();
    };
  }, [originalTop, navHeight]);

  // 2. SCROLL LOGIC
  useEffect(() => {
    const handleScroll = () => {
      const safeNavHeight = mainNavHeight || 0;

      if (window.scrollY < 10) {
        setIsSticky(false);
        return;
      }

      if (originalTop === 0) return;

      const threshold = originalTop - safeNavHeight;
      const shouldBeSticky = window.scrollY >= threshold;

      if (shouldBeSticky !== isSticky) {
        setIsSticky(shouldBeSticky);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [originalTop, isSticky, mainNavHeight]);

  // 3. Auto-Center Active Tab
  useEffect(() => {
    if (activeTabRef.current) {
      activeTabRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeTab]);

  const stickyStyles = {
    transform: "translateZ(0)",
  };

  if (isSticky) {
    stickyStyles.top = `${mainNavHeight || 0}px`;
  }

  return (
    <>
      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}
      </style>

      <div ref={containerRef} className="relative w-full z-20">
        <nav
          className={`transition-[background,box-shadow,padding] duration-300 ease-in-out w-full ${
            isSticky
              ? "fixed left-0 right-0 bg-white/95 backdrop-blur-md shadow-lg"
              : "relative bg-white/95 shadow-none"
          }`}
          style={isSticky ? stickyStyles : {}}
        >
          <div className="container mx-auto max-w-6xl px-0">
            <div className="flex overflow-x-auto hide-scrollbar">
              {features.map((feature) => (
                <button
                  key={feature.id}
                  ref={activeTab === feature.id ? activeTabRef : null}
                  onClick={() => onTabClick(feature.id)}
                  className={`
                    relative px-6 md:px-8 text-left font-medium text-base
                    transition-colors duration-300 flex-1
                    flex items-start justify-start
                    min-w-[160px] 
                    py-2 h-20 
                    ${
                      activeTab === feature.id
                        ? "bg-gray-100 text-gray-800"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }
                  `}
                >
                  {feature.title}
                  {activeTab === feature.id && (
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-1.5 bg-green-900"
                      layoutId="underline"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        </nav>
        {isSticky && <div style={{ height: `${navHeight || 80}px` }} />}
      </div>
    </>
  );
};

// --- FeatureSection component ---
const FeatureSection = React.forwardRef(({ feature }, ref) => (
  <div ref={ref} className="container mx-auto max-w-6xl px-4 pt-16 md:pt-24">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center min-h-[450px]">
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5 }}
      >
        {feature.icon && <div className="mb-6">{feature.icon}</div>}
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          {feature.heading}
        </h2>

        <ul className="my-8 p-0 list-none">
          {feature.points.map((point, index) => (
            <li
              key={index}
              className="py-2 border-b border-gray-200 last:border-b-0"
            >
              <span className="text-gray-600 text-sm leading-relaxed font-medium block text-left">
                {point}
              </span>
            </li>
          ))}
        </ul>
        <Link
          to={`/enterprise-security/${feature.id}`}
          className="inline-block bg-green-900 text-white font-semibold mt-2 text-lg px-6 py-2 rounded-md hover:bg-green-700 transition-colors no-underline"
        >
          More Information &rarr;
        </Link>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-center md:justify-end"
      >
        <div className="relative overflow-hidden rounded-2xl group">
          <img
            src={feature.imageUrl}
            alt={feature.heading}
            className="w-full h-auto max-w-md object-contain rounded-2xl transition-transform duration-700 ease-out group-hover:scale-[1.35]"
            draggable={false}
          />
        </div>
      </motion.div>
    </div>
  </div>
));

// --- EnterpriseCyberSecurity MAIN COMPONENT ---
export default function EnterpriseCyberSecurity() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeTab, setActiveTab] = useState(enterpriseFeatures[0].id);
  const [mainNavHeight, setMainNavHeight] = useState(null);

  useEffect(() => {
    const updateNavHeight = () => {
      const mainNav = document.getElementById("main-navbar");
      if (mainNav) {
        setMainNavHeight(mainNav.offsetHeight);
      }
    };
    updateNavHeight();
    window.addEventListener("resize", updateNavHeight);
    return () => window.removeEventListener("resize", updateNavHeight);
  }, []);

  const sectionRefs = useRef(
    enterpriseFeatures.reduce((acc, feature) => {
      acc[feature.id] = React.createRef();
      return acc;
    }, {}),
  );

  useEffect(() => {
    const safeHeight = mainNavHeight || 0;
    const offset = safeHeight + 100;

    const handleScroll = () => {
      let currentTab = activeTab;

      for (const feature of enterpriseFeatures) {
        const ref = sectionRefs.current[feature.id];
        if (ref.current) {
          const { top, bottom } = ref.current.getBoundingClientRect();
          if (top <= offset && bottom >= offset) {
            currentTab = feature.id;
            break;
          }
        }
      }

      if (currentTab !== activeTab) {
        setActiveTab(currentTab);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeTab, mainNavHeight]);

  const handleTabClick = (id) => {
    const ref = sectionRefs.current[id];
    const safeHeight = mainNavHeight || 0;

    if (ref.current) {
      const y =
        ref.current.getBoundingClientRect().top +
        window.scrollY -
        safeHeight -
        80;
      window.scrollTo({ top: y, behavior: "smooth" });
      setActiveTab(id);
    }
  };

  return (
    <>
      <Helmet>
        <title>Enterprise Cyber Security Solutions | Synthera Security</title>

        <meta
          name="description"
          content="Protect your organization with enterprise cyber security solutions from Synthera Security. Prevent threats, reduce risk, secure access, and strengthen resilience."
        />

        <meta
          name="keywords"
          content="Enterprise Cyber Security, Zero Trust Security, Threat Protection, Cyber Defense, Network Security, Enterprise Security Solutions, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Enterprise Cyber Security Solutions | Synthera Security"
        />

        <meta
          property="og:description"
          content="Comprehensive enterprise cyber security solutions designed to stop threats, reduce risk, and secure modern organizations."
        />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/solutions/enterprise-cyber-security"
        />

        <meta property="og:type" content="website" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/solutions/enterprise-cyber-security"
        />
      </Helmet>

      <div className="bg-white text-gray-900 overflow-hidden">
        {/* --- ★ UPDATED GREEN HEADER SECTION ★ --- */}
        <div className="bg-[#032d28]">
          {/* --- 1. Hero Section --- */}
          <div className="w-full bg-[#032d28]">
            {/* Desktop */}
            <div className="hidden md:block">
              <img
                src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1781090771/enterprise_ofegxq.png"
                alt="Enterprise Cybersecurity"
                className="w-full h-auto block"
              />
            </div>

            {/* Mobile */}
            <div className="block md:hidden pt-16 overflow-hidden">
              <img
                src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1781090771/enterprise_ofegxq.png"
                alt="Enterprise Cybersecurity"
                className="w-full h-auto block"
              />
            </div>
          </div>

          {/* --- NEW TOP SECTION START --- */}
          <div className="container mx-auto max-w-6xl px-4 pt-20 md:pt-28 pb-12">
            {/* PART 1: Protect Organization */}
            <div className="mb-20">
              <h1 className="text-white font-bold text-4xl md:text-5xl leading-tight mb-6">
                Protect your organization from the new generation of threats
              </h1>
              <p className="text-lg text-green-100/80 max-w-4xl leading-relaxed mb-16">
                To secure your organization from constantly evolving and
                advancing cyberattacks, you need a platform that delivers
                comprehensive zero trust cyberthreat protection at the speed and
                scale of the cloud.
              </p>

              {/* 4-Column Icon Grid */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12 mb-16">
                {/* Item 1 */}
                <div className="flex flex-col items-start">
                  <div className="text-green-400 mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                  <h3 className="text-white font-bold text-xl leading-snug">
                    Stop threats before they become attacks
                  </h3>
                </div>
                {/* Item 2 */}
                <div className="flex flex-col items-start">
                  <div className="text-green-400 mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="3" />
                      <circle cx="12" cy="5" r="2" />
                      <circle cx="12" cy="19" r="2" />
                      <circle cx="5" cy="12" r="2" />
                      <circle cx="19" cy="12" r="2" />
                    </svg>
                  </div>
                  <h3 className="text-white font-bold text-xl leading-snug">
                    Eliminate point products and complexity
                  </h3>
                </div>
                {/* Item 3 */}
                <div className="flex flex-col items-start">
                  <div className="text-green-400 mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 12h16" />
                      <path d="m16 8 4 4-4 4" />
                      <path d="M8 16 4 12l4-4" />
                    </svg>
                  </div>
                  <h3 className="text-white font-bold text-xl leading-snug">
                    Prevent lateral threat movement
                  </h3>
                </div>
                {/* Item 4 */}
                <div className="flex flex-col items-start">
                  <div className="text-green-400 mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 9V5" />
                      <rect x="11" y="2" width="2" height="2" />
                    </svg>
                  </div>
                  <h3 className="text-white font-bold text-xl leading-snug">
                    See and prioritize your biggest risks
                  </h3>
                </div>
              </div>

              {/* Section Label */}
              <div className="border-b border-white/20 pb-3">
                <span className="text-green-400 font-bold tracking-widest uppercase text-sm">
                  THE PROBLEM
                </span>
              </div>
            </div>

            {/* PART 2: Bank Robbery Analogy */}
            <div>
              <h1 className="text-white font-bold text-4xl md:text-5xl leading-tight mb-12">
                A cyberattack is like a bank robbery
              </h1>

              {/* The Diagram Image */}
              <div className="mb-16">
                <img
                  src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1767099132/img_enterprise_dyy4yk.png"
                  alt="Comparison diagram"
                  className="w-full h-auto max-w-5xl mx-auto rounded-3xl shadow-2xl"
                />
              </div>

              {/* 4-Column Numbered Steps Grid */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12 mb-16">
                {/* Step 1 */}
                <div className="flex flex-col items-start">
                  <div className="text-white/20 font-bold text-3xl mb-2">
                    01
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">
                    Attackers find you
                  </h3>
                  <p className="text-green-100/80 text-base leading-relaxed">
                    Discoverable IPs are vulnerable even firewalls and VPNs,
                    which are meant to protect you in the first place.
                  </p>
                </div>
                {/* Step 2 */}
                <div className="flex flex-col items-start">
                  <div className="text-white/20 font-bold text-3xl mb-2">
                    02
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">
                    They compromise you
                  </h3>
                  <p className="text-green-100/80 text-base leading-relaxed">
                    Attackers exploit vulnerabilities to establish a beachhead
                    in your network.
                  </p>
                </div>
                {/* Step 3 */}
                <div className="flex flex-col items-start">
                  <div className="text-white/20 font-bold text-3xl mb-2">
                    03
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">
                    They move laterally
                  </h3>
                  <p className="text-green-100/80 text-base leading-relaxed">
                    Once a machine is infected, the entire network is
                    compromised.
                  </p>
                </div>
                {/* Step 4 */}
                <div className="flex flex-col items-start">
                  <div className="text-white/20 font-bold text-3xl mb-2">
                    04
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">
                    They steal your data
                  </h3>
                  <p className="text-green-100/80 text-base leading-relaxed">
                    Sensitive data across applications, servers, and entire
                    networks is lost.
                  </p>
                </div>
              </div>

              {/* Section Label */}
              <div className="border-b border-white/20 pb-3">
                <span className="text-green-400 font-bold tracking-widest uppercase text-sm">
                  SOLUTION OVERVIEW
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* --- END OF GREEN HEADER SECTION --- */}

        {/* --- 5. STICKY NAV --- */}
        <StickyTabNav
          features={enterpriseFeatures}
          activeTab={activeTab}
          onTabClick={handleTabClick}
          mainNavHeight={mainNavHeight}
        />

        {/* --- 6. FEATURE SECTIONS --- */}
        <div className="pb-16 md:pb-24">
          {enterpriseFeatures.map((feature, index) => (
            <React.Fragment key={feature.id}>
              {index > 0 && (
                <div className="container mx-auto max-w-6xl px-4">
                  <hr className="border-t-1 border-gray-900" />
                </div>
              )}
              <FeatureSection
                feature={feature}
                ref={sectionRefs.current[feature.id]}
              />
            </React.Fragment>
          ))}
        </div>
        <Footer />
      </div>
    </>
  );
}
